import { useState, useEffect } from 'react';
import {
  fetchTransactions,
  deleteTransaction,
  type TransactionFilters,
} from '@/api/marketplace-transactions.api';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  CheckCircle,
  XCircle,
  Trash2,
  Clock,
  ArrowLeftRight,
  DollarSign,
  ShoppingBag,
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

export default function MarketplaceTransactions() {
  const [transactions, setTransactions] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState<TransactionFilters>({
    limit: 20,
  });
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState<any>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');

  const loadTransactions = async (loadMore = false) => {
    try {
      setLoading(true);
      const currentFilters = loadMore ? { ...filters, cursor: nextCursor } : filters;
      const data = await fetchTransactions(currentFilters);
      if (loadMore) {
        setTransactions((prev) => [...prev, ...(data?.data || [])]);
      } else {
        setTransactions(data?.data || []);
      }
      setNextCursor(data?.nextCursor || null);
      setHasMore(data?.hasMore || false);
    } catch (error) {
      console.error('Failed to load transactions:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTransactions();
  }, [filters]);

  const openDeleteDialog = (transaction: any) => {
    setSelectedTransaction(transaction);
    setDeleteReason('');
    setDeleteDialogOpen(true);
  };

  const handleDelete = async () => {
    if (!selectedTransaction) return;
    try {
      await deleteTransaction(selectedTransaction._id, deleteReason);
      setTransactions((prev) => prev.filter((t) => t._id !== selectedTransaction._id));
      setDeleteDialogOpen(false);
      setSelectedTransaction(null);
      setDeleteReason('');
    } catch (error) {
      console.error('Failed to delete transaction:', error);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusConfig: any = {
      pending: { variant: 'secondary', icon: Clock, color: 'text-yellow-600' },
      completed: { variant: 'default', icon: CheckCircle, color: 'text-green-600' },
      cancelled: { variant: 'destructive', icon: XCircle, color: 'text-red-600' },
    };

    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <Badge variant={config.variant} className="flex items-center gap-1">
        <Icon className={`w-3 h-3 ${config.color}`} />
        {status}
      </Badge>
    );
  };

  const getTypeBadge = (type: string) => {
    if (type === 'sale') {
      return (
        <Badge variant="outline" className="flex items-center gap-1">
          <DollarSign className="w-3 h-3 text-green-600" />
          Sale
        </Badge>
      );
    }
    return (
      <Badge variant="outline" className="flex items-center gap-1">
        <ArrowLeftRight className="w-3 h-3 text-purple-600" />
        Exchange
      </Badge>
    );
  };

  return (
    <div className="container mx-auto p-6 space-y-6 h-full overflow-auto">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Marketplace Transactions</h1>
          <p className="text-muted-foreground">
            View and manage all marketplace transactions
          </p>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardHeader>
          <CardTitle>Filters</CardTitle>
          <CardDescription>Filter transactions by status and type</CardDescription>
        </CardHeader>
        <CardContent className="flex gap-4">
          <Select
            value={filters.status || 'all'}
            onValueChange={(value) =>
              setFilters({ ...filters, status: value === 'all' ? undefined : value as any })
            }
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>

          <Select
            value={filters.transactionType || 'all'}
            onValueChange={(value) =>
              setFilters({ ...filters, transactionType: value === 'all' ? undefined : value as any })
            }
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="sale">Sale</SelectItem>
              <SelectItem value="exchange">Exchange</SelectItem>
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {/* Transactions Table */}
      <Card>
        <CardHeader>
          <CardTitle>Transactions</CardTitle>
          <CardDescription>
            {transactions.length} transaction(s) found
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading && transactions.length === 0 ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
            </div>
          ) : transactions.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No transactions found
            </div>
          ) : (
            <div className="overflow-auto max-h-[600px]">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Listing</TableHead>
                  <TableHead>Buyer</TableHead>
                  <TableHead>Seller</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Price</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Created</TableHead>
                  <TableHead>Completed</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {transactions.map((transaction) => (
                  <TableRow key={transaction._id}>
                    <TableCell className="max-w-xs">
                      <div className="flex items-center gap-2">
                        {transaction.listing?.media?.[0]?.url && (
                          <img
                            src={transaction.listing.media[0].url}
                            alt={transaction.listing?.title}
                            className="w-10 h-10 object-cover rounded"
                          />
                        )}
                        <div className="font-medium line-clamp-1">
                          {transaction.listing?.title || 'Deleted listing'}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <img
                          src={transaction.buyer?.profile || '/avatar.png'}
                          alt={transaction.buyer?.username}
                          className="w-6 h-6 rounded-full"
                        />
                        {transaction.buyer?.username || '-'}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <img
                          src={transaction.seller?.profile || '/avatar.png'}
                          alt={transaction.seller?.username}
                          className="w-6 h-6 rounded-full"
                        />
                        {transaction.seller?.username || '-'}
                      </div>
                    </TableCell>
                    <TableCell>{getTypeBadge(transaction.transactionType)}</TableCell>
                    <TableCell>
                      {transaction.finalPrice ? (
                        <span className="font-semibold">
                          {transaction.listing?.currency || '$'}{transaction.finalPrice}
                        </span>
                      ) : (
                        <span className="text-purple-600">Exchange</span>
                      )}
                    </TableCell>
                    <TableCell>{getStatusBadge(transaction.status)}</TableCell>
                    <TableCell>
                      {new Date(transaction.createdAt).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      {transaction.completedAt
                        ? new Date(transaction.completedAt).toLocaleDateString()
                        : '-'}
                    </TableCell>
                    <TableCell>
                      {transaction.status !== 'cancelled' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => openDeleteDialog(transaction)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </div>
          )}
          {hasMore && !loading && (
            <div className="flex justify-center pt-4">
              <Button variant="outline" onClick={() => loadTransactions(true)}>
                Load More
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="sm:max-w-[525px]">
          <DialogHeader>
            <DialogTitle>
              {selectedTransaction?.status === 'completed'
                ? 'Reverse Transaction'
                : 'Cancel Transaction'}
            </DialogTitle>
            <DialogDescription>
              {selectedTransaction?.status === 'completed'
                ? 'This will reverse the completed transaction and revert sale/exchange counters.'
                : 'This will cancel the pending transaction and reactivate the listing.'}
            </DialogDescription>
          </DialogHeader>

          {selectedTransaction && (
            <div className="space-y-4">
              <div>
                <Label className="text-sm text-muted-foreground">Listing</Label>
                <div className="font-medium">
                  {selectedTransaction.listing?.title || 'Deleted listing'}
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-sm text-muted-foreground">Buyer</Label>
                  <div className="font-medium">{selectedTransaction.buyer?.username || '-'}</div>
                </div>
                <div>
                  <Label className="text-sm text-muted-foreground">Seller</Label>
                  <div className="font-medium">{selectedTransaction.seller?.username || '-'}</div>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="deleteReason">Reason *</Label>
                <Textarea
                  id="deleteReason"
                  placeholder="Explain why this transaction is being cancelled/reversed..."
                  value={deleteReason}
                  onChange={(e) => setDeleteReason(e.target.value)}
                  rows={3}
                />
              </div>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleDelete}
              disabled={!deleteReason.trim()}
              variant="destructive"
            >
              {selectedTransaction?.status === 'completed' ? 'Reverse' : 'Cancel Transaction'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
